import React, { useState } from 'react';
import { SearchIcon, FilterIcon, StarIcon, MapPinIcon, CalendarIcon, MessageSquareIcon, XIcon, CheckIcon, BriefcaseIcon, ClockIcon, BookmarkIcon, ChevronRightIcon, GraduationCapIcon, HeartIcon, UserIcon } from 'lucide-react';
interface Mentor {
  id: number;
  name: string;
  image: string;
  role: string;
  company: string;
  location: string;
  yearsOfExperience: number;
  availability: string;
  categories: string[];
  bio: string;
  match: number;
  status: 'available' | 'pending' | 'matched';
  skills: string[];
  education: string;
  languages: string[];
  menteeCount: number;
  testimonials: {
    text: string;
    author: string;
    role: string;
  }[];
  schedule: {
    day: string;
    slots: string[];
  }[];
}
interface MenteeMatchingProps {
  userType: 'mentor' | 'mentee' | null;
}
const MatchScoreTooltip = ({
  score,
  isVisible,
  onClose
}: {
  score: number;
  isVisible: boolean;
  onClose: () => void;
}) => {
  if (!isVisible) return null;
  return <div className="fixed inset-0 flex items-center justify-center z-50" onClick={onClose} data-id="element-1318">
      <div className="absolute inset-0 bg-black opacity-50" data-id="element-1319"></div>
      <div className="bg-white rounded-xl p-6 max-w-sm relative z-10" onClick={e => e.stopPropagation()} data-id="element-1320">
        <h3 className="text-lg font-semibold mb-4 flex items-center" data-id="element-1321">
          <StarIcon size={20} className="text-purple-600 mr-2" data-id="element-1322" />
          {score}% Match Score
        </h3>
        <p className="text-gray-600 text-sm mb-4" data-id="element-1323">
          Your match score is calculated based on:
        </p>
        <ul className="space-y-3 mb-4" data-id="element-1324">
          <li className="flex items-start text-sm" data-id="element-1325">
            <span className="text-purple-600 mr-2" data-id="element-1326">•</span>
            <span data-id="element-1327">Shared mentorship categories and interests</span>
          </li>
          <li className="flex items-start text-sm" data-id="element-1328">
            <span className="text-purple-600 mr-2" data-id="element-1329">•</span>
            <span data-id="element-1330">Communication style compatibility</span>
          </li>
          <li className="flex items-start text-sm" data-id="element-1331">
            <span className="text-purple-600 mr-2" data-id="element-1332">•</span>
            <span data-id="element-1333">Availability and scheduling alignment</span>
          </li>
          <li className="flex items-start text-sm" data-id="element-1334">
            <span className="text-purple-600 mr-2" data-id="element-1335">•</span>
            <span data-id="element-1336">Experience level relevance</span>
          </li>
        </ul>
        <button onClick={onClose} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1337">
          Got it
        </button>
      </div>
    </div>;
};
const MenteeMatching: React.FC<MenteeMatchingProps> = ({
  userType
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [experienceRange, setExperienceRange] = useState<[number, number]>([0, 20]);
  const [showRequestSuccess, setShowRequestSuccess] = useState(false);
  const [requestedMentor, setRequestedMentor] = useState<Mentor | null>(null);
  const [showMatchTooltip, setShowMatchTooltip] = useState<number | null>(null);
  const mentors: Mentor[] = [{
    id: 1,
    name: 'Dr. Sarah Chen',
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    role: 'Mental Health Counselor',
    company: 'Wellness Alliance',
    location: 'Remote',
    yearsOfExperience: 8,
    availability: '8 hours/month',
    categories: ['Mental Health', 'Self-Care', 'Work-Life Balance'],
    bio: 'Licensed counselor specializing in LGBTQ+ mental health, anxiety management, and building resilience. Passionate about helping individuals develop healthy coping mechanisms and self-care practices.',
    match: 95,
    status: 'available',
    skills: ['Anxiety Management', 'Mindfulness', 'Stress Reduction', 'CBT Techniques', 'Crisis Support'],
    education: 'PhD in Clinical Psychology, Columbia University',
    languages: ['English', 'Mandarin'],
    menteeCount: 12,
    testimonials: [{
      text: 'Dr. Chen helped me develop practical strategies for managing anxiety and building self-confidence. Her approach is both professional and deeply compassionate.',
      author: 'Alex Kim',
      role: 'Former Mentee'
    }, {
      text: 'Working with Dr. Chen transformed how I approach my mental health. She provided me with tools that I use daily to manage stress and anxiety.',
      author: 'Jamie Rodriguez',
      role: 'Current Mentee'
    }, {
      text: 'I appreciate how Dr. Chen tailors her approach to my specific needs. She truly listens and provides guidance that feels personalized and effective.',
      author: 'Taylor Johnson',
      role: 'Former Mentee'
    }],
    schedule: [{
      day: 'Monday',
      slots: ['10:00 AM', '2:00 PM', '4:00 PM']
    }, {
      day: 'Wednesday',
      slots: ['11:00 AM', '3:00 PM']
    }]
  }, {
    id: 2,
    name: 'Jordan Taylor',
    image: 'https://randomuser.me/api/portraits/nonbinary/9.jpg',
    role: 'Identity Coach & Community Leader',
    company: 'Pride Community Center',
    location: 'Boston, MA',
    yearsOfExperience: 10,
    availability: '10 hours/month',
    categories: ['Identity', 'Coming Out', 'Family Relations'],
    bio: 'Dedicated to supporting individuals in their journey of self-discovery and identity acceptance. Experienced in helping navigate family relationships and workplace transitions.',
    match: 88,
    status: 'available',
    skills: ['Identity Exploration', 'Family Mediation', 'Support Networks', 'Cultural Competency', 'Advocacy'],
    education: 'MA in Gender Studies, NYU',
    languages: ['English', 'Spanish'],
    menteeCount: 15,
    testimonials: [{
      text: "Jordan's guidance was invaluable during my coming out journey. They provided a safe space and practical advice for navigating complex family dynamics.",
      author: 'Sam Rivera',
      role: 'Current Mentee'
    }, {
      text: "I'm grateful for Jordan's support during a challenging time in my life. Their insights helped me communicate better with my family about my identity.",
      author: 'Casey Morgan',
      role: 'Former Mentee'
    }, {
      text: 'Working with Jordan helped me embrace my authentic self. Their approach combines compassion with practical strategies for navigating difficult conversations.',
      author: 'Riley Chen',
      role: 'Former Mentee'
    }],
    schedule: [{
      day: 'Tuesday',
      slots: ['9:00 AM', '3:00 PM']
    }, {
      day: 'Thursday',
      slots: ['11:00 AM', '4:00 PM']
    }]
  }, {
    id: 3,
    name: 'Marcus Wong',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    role: 'Senior Product Manager',
    company: 'Tech Innovators Inc.',
    location: 'San Francisco, CA',
    yearsOfExperience: 12,
    availability: '6 hours/month',
    categories: ['Career Development', 'Leadership', 'Workplace Inclusion'],
    bio: 'Experienced leader focused on helping LGBTQ+ professionals navigate career growth and workplace dynamics. Specializing in tech industry advancement and inclusive leadership.',
    match: 92,
    status: 'available',
    skills: ['Career Planning', 'Leadership Development', 'Workplace Navigation', 'DEI Initiatives', 'Networking'],
    education: 'MBA, Stanford University',
    languages: ['English', 'Cantonese'],
    menteeCount: 8,
    testimonials: [{
      text: "Marcus's guidance helped me navigate coming out at work and advancing my career while being authentically myself.",
      author: 'Jamie Lee',
      role: 'Product Manager'
    }, {
      text: 'Marcus provided invaluable insights about leadership and visibility as an LGBTQ+ professional in tech. His mentorship directly contributed to my recent promotion.',
      author: 'Alex Patel',
      role: 'Engineering Lead'
    }, {
      text: 'Working with Marcus helped me build confidence in my professional identity. His strategies for workplace navigation are both practical and effective.',
      author: 'Jordan Smith',
      role: 'UX Designer'
    }],
    schedule: [{
      day: 'Monday',
      slots: ['5:00 PM', '7:00 PM']
    }, {
      day: 'Friday',
      slots: ['3:00 PM', '6:00 PM']
    }]
  }, {
    id: 4,
    name: 'Elena Rodriguez',
    image: 'https://randomuser.me/api/portraits/women/28.jpg',
    role: 'Relationship Counselor',
    company: 'Love & Growth Center',
    location: 'Miami, FL',
    yearsOfExperience: 15,
    availability: '12 hours/month',
    categories: ['Relationships', 'Communication', 'Personal Growth'],
    bio: 'Specialized in helping LGBTQ+ individuals and couples build healthy relationships, improve communication, and foster personal growth. Expert in navigating relationship dynamics and building lasting connections.',
    match: 85,
    status: 'available',
    skills: ['Relationship Coaching', 'Communication Skills', 'Conflict Resolution', 'Boundary Setting', 'Dating Guidance'],
    education: 'MS in Marriage and Family Therapy',
    languages: ['English', 'Spanish'],
    menteeCount: 20,
    testimonials: [{
      text: "Elena's expertise in relationship dynamics helped me build more meaningful connections and improve my communication skills significantly.",
      author: 'Chris Morgan',
      role: 'Former Mentee'
    }, {
      text: 'Elena taught me how to establish healthy boundaries in my relationships. Her guidance transformed how I approach dating and partnerships.',
      author: 'Sam Taylor',
      role: 'Current Mentee'
    }, {
      text: "Working with Elena helped me and my partner develop stronger communication skills. Her insights were invaluable to our relationship's growth.",
      author: 'Jesse Williams',
      role: 'Former Mentee'
    }],
    schedule: [{
      day: 'Wednesday',
      slots: ['10:00 AM', '2:00 PM', '5:00 PM']
    }, {
      day: 'Saturday',
      slots: ['11:00 AM', '3:00 PM']
    }]
  }];
  const filterMentors = () => {
    return mentors.filter(mentor => {
      const matchesSearch = mentor.name.toLowerCase().includes(searchQuery.toLowerCase()) || mentor.role.toLowerCase().includes(searchQuery.toLowerCase()) || mentor.categories.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategories = selectedCategories.length === 0 || selectedCategories.some(cat => mentor.categories.includes(cat));
      const matchesAvailability = selectedAvailability.length === 0 || selectedAvailability.includes(mentor.availability);
      const matchesExperience = mentor.yearsOfExperience >= experienceRange[0] && mentor.yearsOfExperience <= experienceRange[1];
      return matchesSearch && matchesCategories && matchesAvailability && matchesExperience;
    });
  };
  const MentorCard = ({
    mentor
  }: {
    mentor: Mentor;
  }) => <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-1338">
      <div className="p-4" data-id="element-1339">
        <div className="flex items-start justify-between mb-4" data-id="element-1340">
          <div className="flex items-center" data-id="element-1341">
            <img src={mentor.image} alt={mentor.name} className="w-16 h-16 rounded-full object-cover" data-id="element-1342" />
            <div className="ml-3 max-w-[60%]" data-id="element-1343">
              <h3 className="font-semibold" data-id="element-1344">{mentor.name}</h3>
              <p className="text-sm text-gray-600" data-id="element-1345">{mentor.role}</p>
            </div>
          </div>
          <div data-id="element-1346">
            <button onClick={e => {
            e.stopPropagation();
            setShowMatchTooltip(mentor.id);
          }} className="flex items-center whitespace-nowrap bg-purple-50 px-2 py-1 rounded-full hover:bg-purple-100 transition-colors" data-id="element-1347">
              <StarIcon size={14} className="text-purple-600 mr-1" data-id="element-1348" />
              <span className="text-sm font-medium text-purple-600" data-id="element-1349">
                {mentor.match}% Match
              </span>
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mb-3" data-id="element-1350">
          {mentor.categories.map(category => <span key={category} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full" data-id="element-1351">
              {category}
            </span>)}
        </div>
        <div className="text-sm text-gray-600 mb-4 line-clamp-2 cursor-pointer hover:text-gray-900" onClick={() => setSelectedMentor(mentor)} data-id="element-1352">
          {mentor.bio}
        </div>
        <div className="flex justify-between items-center" data-id="element-1353">
          <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-1354">
            <div className="flex items-center" data-id="element-1355">
              <MapPinIcon size={14} className="mr-1" data-id="element-1356" />
              {mentor.location}
            </div>
            <div className="flex items-center" data-id="element-1357">
              <ClockIcon size={14} className="mr-1" data-id="element-1358" />
              {mentor.availability}
            </div>
          </div>
          <button onClick={() => setSelectedMentor(mentor)} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700" data-id="element-1359">
            View Profile
          </button>
        </div>
      </div>
    </div>;
  const FilterModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1360">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1361">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1362">
          <h3 className="font-semibold" data-id="element-1363">Filter Mentors</h3>
          <button onClick={() => setShowFilters(false)} data-id="element-1364">
            <XIcon size={20} data-id="element-1365" />
          </button>
        </div>
        <div className="p-4" data-id="element-1366">
          <div className="mb-4" data-id="element-1367">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1368">
              Categories
            </label>
            <div className="grid grid-cols-2 gap-2" data-id="element-1369">
              {['Career', 'Tech Leadership', 'UX Design', 'Work-Life Balance', 'Personal Growth', 'Identity'].map(category => <label key={category} className="flex items-center" data-id="element-1370">
                  <input type="checkbox" checked={selectedCategories.includes(category)} onChange={() => {
                setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-1371" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-1372">{category}</span>
                </label>)}
            </div>
          </div>
          <div className="mb-4" data-id="element-1373">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1374">
              Availability
            </label>
            <div className="space-y-2" data-id="element-1375">
              {['4 hours/month', '8 hours/month', '10 hours/month', '12+ hours/month'].map(availability => <label key={availability} className="flex items-center" data-id="element-1376">
                  <input type="checkbox" checked={selectedAvailability.includes(availability)} onChange={() => {
                setSelectedAvailability(prev => prev.includes(availability) ? prev.filter(a => a !== availability) : [...prev, availability]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-1377" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-1378">
                    {availability}
                  </span>
                </label>)}
            </div>
          </div>
          <div className="mb-6" data-id="element-1379">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1380">
              Years of Experience
            </label>
            <div className="px-2" data-id="element-1381">
              <input type="range" min="0" max="20" value={experienceRange[1]} onChange={e => setExperienceRange([experienceRange[0], parseInt(e.target.value)])} className="w-full accent-purple-600" data-id="element-1382" />
              <div className="flex justify-between text-sm text-gray-600" data-id="element-1383">
                <span data-id="element-1384">{experienceRange[0]}+ years</span>
                <span data-id="element-1385">{experienceRange[1]} years</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-1386">
            <button onClick={() => {
            setSelectedCategories([]);
            setSelectedAvailability([]);
            setExperienceRange([0, 20]);
          }} className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg" data-id="element-1387">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1388">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>;
  const MentorProfileModal = ({
    mentor
  }: {
    mentor: Mentor;
  }) => {
    const [expandedTestimonials, setExpandedTestimonials] = useState(false);
    const hasMultipleTestimonials = mentor.testimonials.length > 1;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1389">
        <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-1390">
          <div className="p-6" data-id="element-1391">
            {/* Header Section */}
            <div className="flex justify-between items-start mb-6" data-id="element-1392">
              <div className="flex items-start" data-id="element-1393">
                <img src={mentor.image} alt={mentor.name} className="w-20 h-20 rounded-full object-cover" data-id="element-1394" />
                <div className="ml-4" data-id="element-1395">
                  <h2 className="text-xl font-semibold" data-id="element-1396">{mentor.name}</h2>
                  <p className="text-gray-600 mb-1" data-id="element-1397">{mentor.role}</p>
                  <button onClick={() => setShowMatchTooltip(mentor.id)} className="inline-flex items-center px-3 py-1 bg-[#F5F0FF] rounded-full hover:bg-purple-100 transition-colors" data-id="element-1398">
                    <StarIcon size={16} className="text-purple-600 mr-1.5" data-id="element-1399" />
                    <span className="text-sm font-medium text-purple-600" data-id="element-1400">
                      {mentor.match}% Match
                    </span>
                  </button>
                </div>
              </div>
              <button onClick={() => setSelectedMentor(null)} data-id="element-1401">
                <XIcon size={16} className="text-gray-500" data-id="element-1402" />
              </button>
            </div>
            {/* About Section */}
            <div className="mb-6" data-id="element-1403">
              <h3 className="font-medium mb-3" data-id="element-1404">About</h3>
              <p className="text-gray-600" data-id="element-1405">{mentor.bio}</p>
            </div>
            {/* Info Tiles Grid */}
            <div className="grid grid-cols-1 gap-4 mb-6" data-id="element-1406">
              {/* Education & Experience Tile */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1407">
                <h3 className="font-medium mb-3" data-id="element-1408">Education & Experience</h3>
                <div className="space-y-2.5" data-id="element-1409">
                  <div className="flex items-center text-gray-600" data-id="element-1410">
                    <BriefcaseIcon size={16} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-1411" />
                    <span className="text-sm" data-id="element-1412">{mentor.company}</span>
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-1413">
                    <BriefcaseIcon size={16} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-1414" />
                    <span className="text-sm" data-id="element-1415">
                      {mentor.yearsOfExperience} years of professional
                      experience
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-1416">
                    <GraduationCapIcon size={16} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-1417" />
                    <span className="text-sm" data-id="element-1418">{mentor.education}</span>
                  </div>
                </div>
              </div>
              {/* Availability Tile */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1419">
                <h3 className="font-medium mb-3" data-id="element-1420">Availability</h3>
                <div className="space-y-2.5" data-id="element-1421">
                  <div className="flex items-center text-gray-600" data-id="element-1422">
                    <ClockIcon size={16} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-1423" />
                    <span className="text-sm whitespace-nowrap" data-id="element-1424">
                      {mentor.availability}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-1425">
                    <MapPinIcon size={16} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-1426" />
                    <span className="text-sm" data-id="element-1427">{mentor.location}</span>
                  </div>
                  <button onClick={() => setShowScheduleModal(true)} className="w-full text-center text-purple-600 hover:text-purple-700 text-sm font-medium mt-2" data-id="element-1428">
                    View Schedule
                  </button>
                </div>
              </div>
            </div>
            {/* Expertise & Languages Grid */}
            <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-1429">
              <div data-id="element-1430">
                <h3 className="font-medium mb-3" data-id="element-1431">Expertise</h3>
                <div className="flex flex-wrap gap-2" data-id="element-1432">
                  {mentor.skills.map(skill => <span key={skill} className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full inline-flex items-center" data-id="element-1433">
                      {skill}
                    </span>)}
                </div>
              </div>
              <div data-id="element-1434">
                <h3 className="font-medium mb-3" data-id="element-1435">Languages</h3>
                <div className="flex flex-wrap gap-2" data-id="element-1436">
                  {mentor.languages.map(language => <span key={language} className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full inline-flex items-center" data-id="element-1437">
                      {language}
                    </span>)}
                </div>
              </div>
            </div>
            {/* Testimonials Section - Enhanced with expand/collapse */}
            {mentor.testimonials.length > 0 && <div className="mb-6" data-id="element-1438">
                <div className="flex justify-between items-center mb-3" data-id="element-1439">
                  <h3 className="font-medium" data-id="element-1440">Testimonials</h3>
                  {hasMultipleTestimonials && <button onClick={() => setExpandedTestimonials(!expandedTestimonials)} className="text-sm text-purple-600 hover:text-purple-700 flex items-center" data-id="element-1441">
                      {expandedTestimonials ? 'Show less' : `+ ${mentor.testimonials.length - 1} more`}
                    </button>}
                </div>
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-1442">
                  {/* Always show the first testimonial */}
                  <div className="mb-4" data-id="element-1443">
                    <p className="text-gray-600 italic mb-2" data-id="element-1444">
                      "{mentor.testimonials[0].text}"
                    </p>
                    <div className="text-sm" data-id="element-1445">
                      <span className="font-medium" data-id="element-1446">
                        {mentor.testimonials[0].author}
                      </span>
                      <span className="text-gray-500" data-id="element-1447">
                        {' '}
                        · {mentor.testimonials[0].role}
                      </span>
                    </div>
                  </div>
                  {/* Show additional testimonials if expanded */}
                  {expandedTestimonials && mentor.testimonials.slice(1).map((testimonial, index) => <div key={index} className="mb-4 last:mb-0 pt-4 border-t border-gray-200" data-id="element-1448">
                        <p className="text-gray-600 italic mb-2" data-id="element-1449">
                          "{testimonial.text}"
                        </p>
                        <div className="text-sm" data-id="element-1450">
                          <span className="font-medium" data-id="element-1451">
                            {testimonial.author}
                          </span>
                          <span className="text-gray-500" data-id="element-1452">
                            {' '}
                            · {testimonial.role}
                          </span>
                        </div>
                      </div>)}
                </div>
              </div>}
            {/* CTA Buttons */}
            <div className="flex flex-col gap-3" data-id="element-1453">
              <button onClick={() => {
              setRequestedMentor(mentor);
              setShowRequestSuccess(true);
              setSelectedMentor(null);
              // Save to localStorage for demo
              const pendingRequests = JSON.parse(localStorage.getItem('pendingRequests') || '[]');
              localStorage.setItem('pendingRequests', JSON.stringify([...pendingRequests, {
                mentorId: mentor.id,
                mentorName: mentor.name,
                mentorImage: mentor.image,
                status: 'pending',
                date: new Date().toISOString()
              }]));
            }} className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center font-medium" data-id="element-1454">
                <MessageSquareIcon size={16} className="mr-2" data-id="element-1455" />
                Request Mentorship
              </button>
              <div className="grid grid-cols-2 gap-3" data-id="element-1456">
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center" data-id="element-1457">
                  <BookmarkIcon size={16} className="mr-2" data-id="element-1458" />
                  Save Profile
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center" data-id="element-1459">
                  <MessageSquareIcon size={16} className="mr-2" data-id="element-1460" />
                  Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  const ScheduleModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1461">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1462">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1463">
          <h3 className="font-semibold" data-id="element-1464">Available Time Slots</h3>
          <button onClick={() => setShowScheduleModal(false)} data-id="element-1465">
            <XIcon size={20} data-id="element-1466" />
          </button>
        </div>
        <div className="p-4" data-id="element-1467">
          {selectedMentor?.schedule.map(day => <div key={day.day} className="mb-4 last:mb-0" data-id="element-1468">
              <h4 className="font-medium text-sm mb-2" data-id="element-1469">{day.day}</h4>
              <div className="grid grid-cols-3 gap-2" data-id="element-1470">
                {day.slots.map(slot => <button key={slot} className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-purple-500 hover:text-purple-600" data-id="element-1471">
                    {slot}
                  </button>)}
              </div>
            </div>)}
          <div className="mt-6 flex justify-end" data-id="element-1472">
            <button onClick={() => setShowScheduleModal(false)} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1473">
              Request Time Slot
            </button>
          </div>
        </div>
      </div>
    </div>;
  const RequestSuccessModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1474">
      <div className="bg-white rounded-xl w-full max-w-md p-6 text-center" data-id="element-1475">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1476">
          <CheckIcon size={32} className="text-green-600" data-id="element-1477" />
        </div>
        <h3 className="text-xl font-semibold mb-2" data-id="element-1478">Request Sent!</h3>
        <p className="text-gray-600 mb-6" data-id="element-1479">
          Your mentorship request has been sent to {requestedMentor?.name}.
          They'll review it and respond soon.
        </p>
        <div className="flex gap-3" data-id="element-1480">
          <button onClick={() => {
          setShowRequestSuccess(false);
          navigate('/messages');
        }} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-1481">
            View in Messages
          </button>
          <button onClick={() => setShowRequestSuccess(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1482">
            Keep Browsing
          </button>
        </div>
      </div>
    </div>;
  return <div className="pb-4" data-id="element-1483">
      <div className="mb-6" data-id="element-1484">
        <h1 className="text-xl font-bold mb-2" data-id="element-1485">Find Your Mentor</h1>
        <div className="flex items-center space-x-4" data-id="element-1486">
          <div className="flex-1 relative" data-id="element-1487">
            <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1488" />
            <input type="text" placeholder="Search by name, role, or expertise..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1489" />
          </div>
          <button onClick={() => setShowFilters(true)} className="px-4 py-2 border border-gray-300 rounded-lg flex items-center hover:bg-gray-50" data-id="element-1490">
            <FilterIcon size={18} className="mr-2" data-id="element-1491" />
            Filters
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1492">
        {filterMentors().map(mentor => <MentorCard key={mentor.id} mentor={mentor} data-id="element-1493" />)}
      </div>
      {filterMentors().length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-1494">
          No mentors found matching your criteria
        </div>}
      {showFilters && <FilterModal data-id="element-1495" />}
      {selectedMentor && <MentorProfileModal mentor={selectedMentor} data-id="element-1496" />}
      {showScheduleModal && selectedMentor && <ScheduleModal data-id="element-1497" />}
      {showRequestSuccess && <RequestSuccessModal data-id="element-1498" />}
      {showMatchTooltip !== null && <MatchScoreTooltip score={mentors.find(m => m.id === showMatchTooltip)?.match || 0} isVisible={showMatchTooltip !== null} onClose={() => setShowMatchTooltip(null)} data-id="element-1499" />}
    </div>;
};
export default MenteeMatching;